import React, { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Target, TrendingUp, Award, XCircle, Trophy, Coins, Save } from "lucide-react";

const gbp = (n) => "£" + Math.round(Number(n) || 0).toLocaleString();

const WIN_REASONS = [
  ["evidence_proof", "Strength of evidence / System of Proof"],
  ["price", "Price / commercials"],
  ["relationship", "Relationship / trust"],
  ["capability", "Capability & credentials"],
  ["speed", "Speed to start / responsiveness"],
  ["incumbent", "Incumbent advantage"],
  ["other", "Other"],
];
const LOSS_REASONS = [
  ["price", "Price / commercials"],
  ["no_decision", "No decision made (status quo)"],
  ["competitor", "Lost to a competitor"],
  ["capability_gap", "Perceived capability gap"],
  ["timing", "Timing / not now"],
  ["budget_cut", "Budget cut / withdrawn"],
  ["other", "Other"],
];
const DEFER_REASONS = [
  ["budget_cut", "Budget cut / withdrawn"],
  ["timing", "Timing / not now"],
  ["no_decision", "No decision made (status quo)"],
  ["reorg", "Client reorganisation"],
  ["other", "Other"],
];
const REASONS = { won: WIN_REASONS, lost: LOSS_REASONS, deferred: DEFER_REASONS };

function Kpi({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="border ek-line ek-surface p-4 rounded-lg" data-testid={`loop-kpi-${label}`}>
      <div className="flex items-center gap-2 text-[10.5px] font-medium tracking-[0.16em] ek-text-muted uppercase mb-1">
        {Icon && <Icon size={13} />}{label}
      </div>
      <div className={`text-2xl font-medium tabular-nums ${tone || "text-[#7737F6]"}`}>{value}</div>
      {sub ? <div className="text-[11px] ek-text-muted mt-1">{sub}</div> : null}
    </div>
  );
}

function ReasonBar({ title, data, tone }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  if (!entries.length) return null;
  return (
    <div data-testid={`loop-reasons-${title}`}>
      <div className="text-[12px] font-semibold tracking-[0.14em] uppercase ek-text-muted mb-2">{title}</div>
      <div className="space-y-1.5">
        {entries.map(([label, v]) => (
          <div key={label} className="flex items-center gap-3">
            <span className="text-[12px] ek-text-soft w-56 truncate">{label}</span>
            <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div className={`h-full ${tone}`} style={{ width: `${(v / max) * 100}%` }} />
            </div>
            <span className="text-[12px] ek-text-muted tabular-nums w-6 text-right">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DecisionForm({ canWrite, onDone }) {
  const [opps, setOpps] = useState([]);
  const [f, setF] = useState({ opportunity_id: "", result: "won", reason_code: "evidence_proof",
    competitor: "", notes: "", decided_value: "" });
  useEffect(() => {
    api.get("/admin/crm/opportunities").then(({ data }) => {
      setOpps((data.opportunities || []).filter((o) => !["Won", "Lost", "Deferred"].includes(o.stage)));
    }).catch(() => {});
  }, []);
  const reasons = REASONS[f.result] || WIN_REASONS;
  useEffect(() => { setF((s) => ({ ...s, reason_code: (REASONS[s.result] || WIN_REASONS)[0][0] })); }, [f.result]);

  const submit = async () => {
    if (!f.opportunity_id) return toast.error("Pick an opportunity");
    try {
      await api.post(`/admin/crm/opportunities/${f.opportunity_id}/decision`, {
        result: f.result, reason_code: f.reason_code, competitor: f.competitor,
        notes: f.notes, decided_value: f.decided_value === "" ? null : Number(f.decided_value),
      });
      toast.success("Decision recorded");
      setF((s) => ({ ...s, opportunity_id: "", competitor: "", notes: "", decided_value: "" }));
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record decision"); }
  };
  if (!canWrite) return null;
  return (
    <div className="border ek-line ek-surface-2 rounded-lg p-4 space-y-3" data-testid="loop-decision-form">
      <div className="text-[13px] font-semibold ek-text">Record a win / loss decision</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <select data-testid="loop-decision-opp" value={f.opportunity_id}
                onChange={(e) => setF((s) => ({ ...s, opportunity_id: e.target.value }))}
                className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface">
          <option value="">Select an open opportunity…</option>
          {opps.map((o) => <option key={o.id} value={o.id}>{o.name} · {gbp(o.est_value)}</option>)}
        </select>
        <select data-testid="loop-decision-result" value={f.result}
                onChange={(e) => setF((s) => ({ ...s, result: e.target.value }))}
                className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface">
          <option value="won">Won</option>
          <option value="lost">Lost</option>
          <option value="deferred">Deferred</option>
        </select>
        <select data-testid="loop-decision-reason" value={f.reason_code}
                onChange={(e) => setF((s) => ({ ...s, reason_code: e.target.value }))}
                className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface">
          {reasons.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="loop-decision-value" type="number" placeholder="Final value (£, optional)"
               value={f.decided_value} onChange={(e) => setF((s) => ({ ...s, decided_value: e.target.value }))}
               className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface" />
        {f.result === "lost" && (
          <input data-testid="loop-decision-competitor" placeholder="Competitor (optional)"
                 value={f.competitor} onChange={(e) => setF((s) => ({ ...s, competitor: e.target.value }))}
                 className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface" />
        )}
        <input data-testid="loop-decision-notes" placeholder="Notes (optional)"
               value={f.notes} onChange={(e) => setF((s) => ({ ...s, notes: e.target.value }))}
               className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface sm:col-span-2" />
      </div>
      <button onClick={submit} data-testid="loop-decision-submit"
              className="px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg">Record decision</button>
    </div>
  );
}

function OutcomeEditor({ canWrite }) {
  const [engs, setEngs] = useState([]);
  const [eid, setEid] = useState("");
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/enterprise360/clients").then(({ data }) => setEngs(data.items || [])).catch(() => {});
  }, []);
  const load = useCallback(async (id) => {
    if (!id) { setO(null); return; }
    try {
      const { data } = await api.get(`/admin/crm/engagements/${id}/outcome`);
      setO({
        pricing_model: data.pricing_model || "fixed_fee",
        pricing_enabled: !!data.pricing_enabled,
        base_fee: data.base_fee || 0, share_pct: data.share_pct || 0,
        cap: data.cap ?? "", baseline_value: data.baseline_value || 0,
        target_value: data.target_value ?? "", realised_value: data.realised_value ?? "",
        metric_label: data.metric_label || "", measurement_date: data.measurement_date || "",
        diagnostic_value_at_stake: data.diagnostic_value_at_stake,
        pricing: data.pricing,
        client_pdf_enabled: !!data.client_pdf_enabled,
        retainer_enabled: !!data.retainer_enabled,
        retainer_tier: data.retainer_tier || "", retainer_monthly_fee: data.retainer_monthly_fee || 0,
        retainer_cadence: data.retainer_cadence || "monthly",
        retainer_term_months: data.retainer_term_months || 0,
        retainer_scope: (data.retainer_scope || []).join("\n"),
        retainer: data.retainer,
      });
    } catch { toast.error("Could not load outcome"); }
  }, []);
  useEffect(() => { load(eid); }, [eid, load]);

  const downloadOutcomePdf = async (id) => {
    try {
      const res = await api.get(`/admin/crm/engagements/${id}/outcome/value-delivered.pdf`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Value_Delivered_${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Enable & save the client PDF first" : "Could not generate PDF");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        pricing_model: o.pricing_model, pricing_enabled: o.pricing_enabled,
        base_fee: Number(o.base_fee) || 0, share_pct: Number(o.share_pct) || 0,
        cap: o.cap === "" ? null : Number(o.cap),
        baseline_value: Number(o.baseline_value) || 0,
        target_value: o.target_value === "" ? null : Number(o.target_value),
        realised_value: o.realised_value === "" ? null : Number(o.realised_value),
        metric_label: o.metric_label, measurement_date: o.measurement_date || null,
        client_pdf_enabled: !!o.client_pdf_enabled,
        retainer_enabled: !!o.retainer_enabled,
        retainer_tier: o.retainer_tier || "",
        retainer_monthly_fee: Number(o.retainer_monthly_fee) || 0,
        retainer_cadence: o.retainer_cadence || "monthly",
        retainer_term_months: Number(o.retainer_term_months) || 0,
        retainer_scope: (o.retainer_scope || "").split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.put(`/admin/crm/engagements/${eid}/outcome`, payload);
      setO((s) => ({ ...s, pricing: data.pricing, retainer: data.retainer }));
      toast.success("Outcome saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const isOutcomeModel = o && o.pricing_model !== "fixed_fee";
  return (
    <div className="border ek-line ek-surface-2 rounded-lg p-4 space-y-3" data-testid="loop-outcome-editor">
      <div className="text-[13px] font-semibold ek-text">Delivered outcome & pricing model</div>
      <select data-testid="loop-outcome-engagement" value={eid} onChange={(e) => setEid(e.target.value)}
              className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full">
        <option value="">Select an engagement…</option>
        {engs.map((e) => <option key={e.id} value={e.id}>{e.client_name} · {e.sector}</option>)}
      </select>
      {o && (
        <>
          {o.diagnostic_value_at_stake != null && (
            <div className="text-[12px] ek-text-muted">Diagnostic value at stake: <strong>{gbp(o.diagnostic_value_at_stake)}</strong></div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-[12px] ek-text-soft">Pricing model
              <select data-testid="loop-outcome-model" value={o.pricing_model}
                      onChange={(e) => setO((s) => ({ ...s, pricing_model: e.target.value }))}
                      className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full">
                <option value="fixed_fee">Fixed fee (default)</option>
                <option value="gain_share">Gain-share (base + % above baseline)</option>
                <option value="pure_outcome">Pure outcome (% of realised)</option>
              </select>
            </label>
            {isOutcomeModel && (
              <label className="flex items-center gap-2 text-[12.5px] ek-text-soft mt-6">
                <input type="checkbox" data-testid="loop-outcome-enabled" checked={o.pricing_enabled}
                       onChange={(e) => setO((s) => ({ ...s, pricing_enabled: e.target.checked }))} />
                Outcome pricing agreed & active (else falls back to fixed fee)
              </label>
            )}
            <label className="text-[12px] ek-text-soft">Base fee (£)
              <input data-testid="loop-outcome-base" type="number" value={o.base_fee}
                     onChange={(e) => setO((s) => ({ ...s, base_fee: e.target.value }))}
                     className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
            {isOutcomeModel && (
              <label className="text-[12px] ek-text-soft">Share %
                <input data-testid="loop-outcome-share" type="number" value={o.share_pct}
                       onChange={(e) => setO((s) => ({ ...s, share_pct: e.target.value }))}
                       className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
            )}
            <label className="text-[12px] ek-text-soft">Baseline value (£)
              <input data-testid="loop-outcome-baseline" type="number" value={o.baseline_value}
                     onChange={(e) => setO((s) => ({ ...s, baseline_value: e.target.value }))}
                     className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
            <label className="text-[12px] ek-text-soft">Realised value (£)
              <input data-testid="loop-outcome-realised" type="number" value={o.realised_value}
                     onChange={(e) => setO((s) => ({ ...s, realised_value: e.target.value }))}
                     className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
            {isOutcomeModel && (
              <label className="text-[12px] ek-text-soft">Fee cap (£, optional)
                <input data-testid="loop-outcome-cap" type="number" value={o.cap}
                       onChange={(e) => setO((s) => ({ ...s, cap: e.target.value }))}
                       className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
            )}
            <label className="text-[12px] ek-text-soft">Outcome metric
              <input data-testid="loop-outcome-metric" placeholder="e.g. £ annual run-rate" value={o.metric_label}
                     onChange={(e) => setO((s) => ({ ...s, metric_label: e.target.value }))}
                     className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
          </div>
          {o.pricing && (
            <div className="border ek-line ek-surface rounded-lg p-3 flex flex-wrap gap-x-8 gap-y-1" data-testid="loop-outcome-preview">
              <div className="text-[12px]"><span className="ek-text-muted">Effective fee:</span> <strong className="text-[#7737F6]">{gbp(o.pricing.computed_fee)}</strong>{o.pricing.capped ? " (capped)" : ""}</div>
              <div className="text-[12px]"><span className="ek-text-muted">Value delivered:</span> {o.pricing.value_delivered == null ? "—" : gbp(o.pricing.value_delivered)}</div>
              <div className="text-[12px]"><span className="ek-text-muted">Model in effect:</span> {o.pricing.model}</div>
            </div>
          )}

          {/* Managed-assurance retainer — optional recurring-revenue capability */}
          <div className="border ek-line rounded-lg p-3 space-y-3" data-testid="loop-retainer">
            <label className="flex items-center gap-2 text-[12.5px] font-medium ek-text">
              <input type="checkbox" data-testid="loop-retainer-enabled" checked={o.retainer_enabled}
                     onChange={(e) => setO((s) => ({ ...s, retainer_enabled: e.target.checked }))} />
              Offer a managed-assurance retainer (optional — off unless the client wants that discussion)
            </label>
            {o.retainer_enabled && (
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-[12px] ek-text-soft">Tier name
                  <input data-testid="loop-retainer-tier" placeholder="e.g. Assurance Essentials" value={o.retainer_tier}
                         onChange={(e) => setO((s) => ({ ...s, retainer_tier: e.target.value }))}
                         className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
                <label className="text-[12px] ek-text-soft">Cadence
                  <select data-testid="loop-retainer-cadence" value={o.retainer_cadence}
                          onChange={(e) => setO((s) => ({ ...s, retainer_cadence: e.target.value }))}
                          className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full">
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select></label>
                <label className="text-[12px] ek-text-soft">Monthly fee (£)
                  <input data-testid="loop-retainer-fee" type="number" value={o.retainer_monthly_fee}
                         onChange={(e) => setO((s) => ({ ...s, retainer_monthly_fee: e.target.value }))}
                         className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
                <label className="text-[12px] ek-text-soft">Term (months)
                  <input data-testid="loop-retainer-term" type="number" value={o.retainer_term_months}
                         onChange={(e) => setO((s) => ({ ...s, retainer_term_months: e.target.value }))}
                         className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
                <label className="text-[12px] ek-text-soft sm:col-span-2">Included scope (one per line)
                  <textarea data-testid="loop-retainer-scope" rows={3} value={o.retainer_scope}
                            onChange={(e) => setO((s) => ({ ...s, retainer_scope: e.target.value }))}
                            className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
                {o.retainer && o.retainer.enabled && (
                  <div className="sm:col-span-2 text-[12px] ek-text-muted" data-testid="loop-retainer-arr">
                    Annualised: <strong className="text-[#7737F6]">{gbp(o.retainer.annualised_value)}</strong>
                    {o.retainer.contract_value ? <> · Contract value: <strong>{gbp(o.retainer.contract_value)}</strong></> : null}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Client-facing value-delivered PDF — off unless enabled */}
          <div className="border ek-line rounded-lg p-3 space-y-2" data-testid="loop-client-pdf">
            <label className="flex items-center gap-2 text-[12.5px] font-medium ek-text">
              <input type="checkbox" data-testid="loop-client-pdf-enabled" checked={o.client_pdf_enabled}
                     onChange={(e) => setO((s) => ({ ...s, client_pdf_enabled: e.target.checked }))} />
              Enable client-facing "Outcome &amp; value delivered" PDF (no fees shown)
            </label>
            {o.client_pdf_enabled && o.pricing?.value_delivered != null && (
              <button type="button" data-testid="loop-client-pdf-download"
                      onClick={() => downloadOutcomePdf(eid)}
                      className="text-[12px] text-[#7737F6] hover:underline">
                Download client outcome PDF ↗
              </button>
            )}
          </div>

          {canWrite && (
            <button onClick={save} disabled={busy} data-testid="loop-outcome-save"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg disabled:opacity-60">
              <Save size={15} /> {busy ? "Saving…" : "Save outcome"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function CommercialLoopPanel({ canWrite }) {
  const [loop, setLoop] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/crm/loop"); setLoop(data); }
    catch { setLoop(null); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const f = loop?.funnel || {};
  const op = loop?.outcome_pricing || {};
  const ret = loop?.retainers || {};

  return (
    <div className="space-y-6" data-testid="crm-loop">
      <div>
        <h3 className="ek-heading text-lg flex items-center gap-2"><Target size={18} /> Commercial Loop</h3>
        <p className="text-[12.5px] ek-text-muted mt-1 max-w-2xl">
          Close the loop from diagnostic to delivered outcome — capture why deals are won and lost, and
          (optionally) price on the value delivered via gain-share or pure-outcome models.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="loop-funnel">
        <Kpi icon={Target} label="Diagnostics" value={f.diagnostics ?? "—"} />
        <Kpi icon={TrendingUp} label="Proposals" value={f.proposals_sent ?? "—"} sub="sent / approved" />
        <Kpi icon={Trophy} label="Won" value={f.won ?? "—"} tone="text-emerald-600" />
        <Kpi icon={XCircle} label="Lost" value={f.lost ?? "—"} tone="text-red-600" />
        <Kpi icon={Award} label="Delivered" value={f.delivered_with_outcome ?? "—"} sub="with outcome" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Win rate" value={loop?.win_rate_pct != null ? `${loop.win_rate_pct}%` : "—"} />
        <Kpi icon={Coins} label="Realised value" value={gbp(op.total_realised_value)} />
        <Kpi label="Value delivered" value={gbp(op.total_value_delivered)} sub="above baselines" />
        <Kpi label="Outcome fees" value={gbp(op.outcome_fee_pipeline)} sub="gain-share / outcome" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Coins} label="Retainer ARR" value={gbp(ret.annual_recurring_revenue)} sub={`${ret.active || 0} active retainer(s)`} />
      </div>

      {loop && (loop.reasons?.won || loop.reasons?.lost) && (
        <div className="border ek-line ek-surface rounded-lg p-4 grid md:grid-cols-2 gap-6">
          <ReasonBar title="Why we win" data={loop.reasons.won} tone="bg-emerald-500" />
          <ReasonBar title="Why we lose" data={loop.reasons.lost} tone="bg-red-400" />
        </div>
      )}

      <DecisionForm canWrite={canWrite} onDone={load} />
      <OutcomeEditor canWrite={canWrite} />
    </div>
  );
}
